import { useState, type FormEvent } from 'react';
import { ApiError, api } from '../api/client';
import type { Bryllupsmedlem, Partnerinvitasjon as Invitasjon } from '../api/types';
import { useResource } from '../hooks/useResource';
import { Alert, Field } from './ui';

/**
 * Å få den andre i brudeparet inn i planleggingen.
 *
 * To steder bruker dette: siste steget i velkomstmodulen, og Konto for den som
 * hoppet over der. Skjemaet er det samme begge steder – det er bare rammen
 * rundt som skiller. Derfor ligger selve utsendingen i `useInvitasjon` her, og
 * ikke i hver av dem.
 *
 * Lenken vises etter utsending. Ikke som en reserveløsning vi skammer oss
 * over, men fordi e-post ikke alltid kommer fram, og fordi mange heller vil
 * sende den på melding. Den kan ikke hentes fram igjen senere: API-et lagrer
 * bare en hash av tokenet, så lenken finnes i det ene svaret og ingen andre
 * steder.
 */

type Oversikt = { medlemmer: Bryllupsmedlem[]; invitasjon: Invitasjon | null };

type Sendt = { invitasjon: Invitasjon; lenke: string; sendt: boolean };

function useInvitasjon(weddingId: string | undefined) {
  const [busy, setBusy] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  const [resultat, setResultat] = useState<Sendt | null>(null);

  const send = async (epost: string, navn: string): Promise<Sendt | null> => {
    setBusy(true);
    setFeil(null);
    try {
      const svar = await api.post<Sendt>(`/weddings/${weddingId}/partner/invitasjon`, {
        email: epost.trim(),
        name: navn.trim() || undefined,
      });
      setResultat(svar);
      return svar;
    } catch (caught) {
      setFeil(
        caught instanceof ApiError ? caught.message : 'Klarte ikke å sende invitasjonen.',
      );
      return null;
    } finally {
      setBusy(false);
    }
  };

  return { send, busy, feil, resultat, nullstill: () => setResultat(null) };
}

/** Lenken, med en knapp som legger den på utklippstavla. */
function Kopilenke({ lenke }: { lenke: string }) {
  const [kopiert, setKopiert] = useState(false);

  return (
    <div className="kopilenke">
      <input className="input" readOnly value={lenke} onFocus={(e) => e.target.select()} />
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => {
          // `clipboard` finnes ikke overalt, og krever HTTPS. Får vi den ikke,
          // står lenken uansett i feltet ved siden av, klar til å merkes.
          void navigator.clipboard
            ?.writeText(lenke)
            .then(() => setKopiert(true))
            .catch(() => undefined);
        }}
      >
        {kopiert ? 'Kopiert' : 'Kopier'}
      </button>
    </div>
  );
}

/** Feltene: navn og e-post. Brukes av begge stedene. */
function Skjema({
  verdier,
  endre,
  busy,
  knapp,
  children,
}: {
  verdier: { navn: string; epost: string };
  endre: (neste: { navn: string; epost: string }) => void;
  busy: boolean;
  knapp: string;
  children?: React.ReactNode;
}) {
  return (
    <>
      <div className="form-grid">
        <Field label="Navn">
          <input
            className="input"
            value={verdier.navn}
            placeholder="Marius"
            autoComplete="off"
            onChange={(event) => endre({ ...verdier, navn: event.target.value })}
          />
        </Field>
        <Field
          label="E-postadresse"
          hint="Vi sender partneren din en invitasjon på e-post."
        >
          <input
            className="input"
            type="email"
            required
            value={verdier.epost}
            placeholder="marius@example.com"
            autoComplete="off"
            onChange={(event) => endre({ ...verdier, epost: event.target.value })}
          />
        </Field>
      </div>
      <div className="partner-knapper">
        <button className="btn btn-primary" type="submit" disabled={busy || !verdier.epost}>
          {busy ? 'Sender …' : knapp}
        </button>
        {children}
      </div>
    </>
  );
}

/**
 * Siste steget i velkomstmodulen.
 *
 * Det kan hoppes over, og det skal være tydelig at det kan det. Den som står
 * her, har nettopp opprettet bryllupet sitt og vil videre – et steg som ser ut
 * som et krav, er et steg folk lukker fanen på.
 */
export function Partnersteg({
  weddingId,
  navnPaaPartner,
  onFerdig,
}: {
  weddingId: string;
  navnPaaPartner: string;
  onFerdig: () => void;
}) {
  const { send, busy, feil, resultat } = useInvitasjon(weddingId);
  const [verdier, setVerdier] = useState({ navn: navnPaaPartner, epost: '' });

  if (resultat) {
    return (
      <>
        <Alert kind="success">
          Invitasjonen er sendt til <strong>{resultat.invitasjon.epost}</strong>.
        </Alert>
        <p className="small muted">
          Du kan også dele invitasjonslenken i en melding. Lenken er gyldig i 14 dager.
        </p>
        <Kopilenke lenke={resultat.lenke} />
        <div className="onboarding-actions">
          <span />
          <button type="button" className="btn btn-primary btn-lg" onClick={onFerdig}>
            Ferdig
          </button>
        </div>
      </>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void send(verdier.epost, verdier.navn);
      }}
    >
      <Alert>{feil}</Alert>
      <Skjema verdier={verdier} endre={setVerdier} busy={busy} knapp="Send invitasjon">
        <button type="button" className="btn btn-ghost" onClick={onFerdig} disabled={busy}>
          Hopp over
        </button>
      </Skjema>
    </form>
  );
}

/**
 * Blokka på Konto, for den som hoppet over i velkomstmodulen.
 *
 * Viser hvem som er med fra før. Er dere allerede to, er det ingenting å gjøre
 * her, og da er et skjema bare noe å lure på.
 */
export function Partnerblokk({ weddingId }: { weddingId: string }) {
  const { data, loading, reload } = useResource<Oversikt>(
    () => api.get<Oversikt>(`/weddings/${weddingId}/partner`),
    [weddingId],
  );
  const { send, busy, feil, resultat, nullstill } = useInvitasjon(weddingId);
  const [verdier, setVerdier] = useState({ navn: '', epost: '' });
  const [angrer, setAngrer] = useState(false);

  if (loading || !data) return <p className="small muted">Henter …</p>;

  const andre = data.medlemmer.filter((medlem) => !medlem.deg);
  const invitasjon = data.invitasjon;

  const sendInn = async (event: FormEvent) => {
    event.preventDefault();
    const svar = await send(verdier.epost, verdier.navn);
    if (svar) {
      setVerdier({ navn: '', epost: '' });
      await reload();
    }
  };

  const angre = async () => {
    setAngrer(true);
    try {
      await api.del(`/weddings/${weddingId}/partner/invitasjon`);
      nullstill();
      await reload();
    } finally {
      setAngrer(false);
    }
  };

  // Allerede to: da er alt som skulle skje her, skjedd.
  if (andre.length > 0) {
    return (
      <>
        <p className="small muted">Dere deler planleggingen:</p>
        <ul className="partnerliste">
          {data.medlemmer.map((medlem) => (
            <li key={medlem.id}>
              <strong>{medlem.navn}</strong>
              <span className="muted"> · {medlem.epost}</span>
              {medlem.deg ? <span className="merke">deg</span> : null}
            </li>
          ))}
        </ul>
      </>
    );
  }

  // Ligger det en ute, er spørsmålet ikke «hvem?», men «kom den fram?».
  if (invitasjon) {
    return (
      <>
        {resultat ? (
          <>
            <Alert kind="success">
              Invitasjonen er sendt til <strong>{resultat.invitasjon.epost}</strong>.
            </Alert>
            <p className="small muted">
              Du kan også dele invitasjonslenken i en melding. Lenken er gyldig i 14 dager.
            </p>
            <Kopilenke lenke={resultat.lenke} />
          </>
        ) : (
          <p className="small muted">
            Invitasjonen er sendt til <strong>{invitasjon.epost}</strong>, og venter på svar.
            Lenken virker til {new Date(invitasjon.utloper).toLocaleDateString('nb-NO')}.
          </p>
        )}

        <Alert>{feil}</Alert>
        <div className="partner-knapper">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => {
              void send(invitasjon.epost, invitasjon.navn ?? '').then((svar) =>
                svar ? reload() : undefined,
              );
            }}
          >
            {busy ? 'Sender …' : 'Send på nytt'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={angrer}
            onClick={() => void angre()}
          >
            {angrer ? 'Angrer …' : 'Angre invitasjonen'}
          </button>
        </div>
        <p className="hint">
          Skrev du feil adresse? Angre invitasjonen, så kan du sende en ny.
        </p>
      </>
    );
  }

  return (
    <>
      <p className="small muted">
        Inviter partneren din og planlegg sammen. Med hver deres innlogging deler dere hele
        planleggingen på Vår Dag – alle verktøyene og alt dere legger inn.
      </p>

      <form onSubmit={sendInn}>
        <Alert>{feil}</Alert>
        <Skjema verdier={verdier} endre={setVerdier} busy={busy} knapp="Send invitasjon" />
      </form>
    </>
  );
}
